import { useEffect, useRef, useState, type DragEvent, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { CircleAlert, CircleCheck, Inbox, Loader2, RotateCcw, ShieldAlert, Upload, X } from "@fg/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApiError, driveApi, errorMessage, isAbortError, type PublicFileRequest } from "@/api/driveApi";
import { formatBytes, formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ItemIcon } from "@/features/drive/items/ItemIcon";

/**
 * Public "Request files" page (/request/:token). No sign-in: the visitor
 * gives a name + email and uploads one or more files straight into the
 * requester's folder — they never see what's already in it. Rendered
 * OUTSIDE ProtectedRoute, styled like the public share page.
 */
type PageStatus = "loading" | "ok" | "closed" | "notfound" | "offline";
type EntryStatus = "ready" | "uploading" | "done" | "error";

interface Entry {
    id: string;
    file: File;
    status: EntryStatus;
    progress: number;
    error?: string;
    /** Size / emptiness problems can't be fixed by retrying. */
    fatal?: boolean;
}

const UPLOADER_KEY = "drive.fileRequest.uploader";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
let seq = 0;

function readUploader(): { name: string; email: string } {
    try {
        const v = JSON.parse(localStorage.getItem(UPLOADER_KEY) ?? "null") as { name?: unknown; email?: unknown } | null;
        return { name: typeof v?.name === "string" ? v.name : "", email: typeof v?.email === "string" ? v.email : "" };
    } catch {
        return { name: "", email: "" };
    }
}

function saveUploader(name: string, email: string) {
    try {
        localStorage.setItem(UPLOADER_KEY, JSON.stringify({ name, email }));
    } catch {
        /* optional convenience */
    }
}

export function FileRequestPage() {
    const { token } = useParams<{ token: string }>();
    const [status, setStatus] = useState<PageStatus>("loading");
    const [request, setRequest] = useState<PublicFileRequest | null>(null);
    const [name, setName] = useState(() => readUploader().name);
    const [email, setEmail] = useState(() => readUploader().email);
    const [entries, setEntries] = useState<Entry[]>([]);
    const [busy, setBusy] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const fileInput = useRef<HTMLInputElement>(null);
    const ctrl = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!token) {
            setStatus("notfound");
            return;
        }
        let cancelled = false;
        void driveApi.resolveFileRequest(token).then(({ status: s, request: r }) => {
            if (cancelled) return;
            if (s === 200 && r) {
                setRequest(r);
                setStatus("ok");
            } else if (s === 410) setStatus("closed");
            else if (s === 0) setStatus("offline");
            else setStatus("notfound");
        });
        return () => {
            cancelled = true;
        };
    }, [token]);

    useEffect(() => {
        document.title = request ? `${request.title} · Upload files` : "Upload files · Drive";
    }, [request]);

    // Don't lose an upload in flight to an accidental tab close.
    useEffect(() => {
        if (!busy) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [busy]);

    const maxBytes = request?.maxFileBytes ?? 250 * 1024 * 1024;
    const update = (id: string, patch: Partial<Entry>) => setEntries((es) => es.map((e) => (e.id === id ? { ...e, ...patch } : e)));

    const addFiles = (files: FileList | File[]) => {
        const next: Entry[] = Array.from(files).map((file) => {
            const problem = file.size === 0 ? "This file is empty" : file.size > maxBytes ? `Larger than the ${formatBytes(maxBytes)} limit` : undefined;
            return { id: `f${seq++}`, file, status: problem ? "error" : "ready", progress: 0, error: problem, fatal: Boolean(problem) };
        });
        if (next.length) setFormError(null);
        setEntries((es) => [...es, ...next]);
    };

    const send = async () => {
        if (!token || busy) return;
        const n = name.trim();
        const m = email.trim();
        if (!n) return setFormError("Enter your name.");
        if (!EMAIL_RE.test(m)) return setFormError("Enter a valid email address.");
        const queue = entries.filter((e) => e.status === "ready");
        if (queue.length === 0) return setFormError("Add at least one file.");
        setFormError(null);
        saveUploader(n, m);
        setBusy(true);
        const c = new AbortController();
        ctrl.current = c;
        for (const e of queue) {
            if (c.signal.aborted) break;
            update(e.id, { status: "uploading", progress: 0, error: undefined });
            let last = 0;
            try {
                await driveApi.uploadToFileRequest(token, e.file, { name: n, email: m }, (f) => {
                    if (f - last >= 0.02 || f >= 1) {
                        last = f;
                        update(e.id, { progress: f });
                    }
                }, c.signal);
                update(e.id, { status: "done", progress: 1 });
            } catch (err) {
                if (isAbortError(err)) {
                    update(e.id, { status: "ready", progress: 0 });
                    break;
                }
                update(e.id, { status: "error", error: errorMessage(err) });
                if (err instanceof ApiError && err.status === 410) {
                    setStatus("closed");
                    break;
                }
            }
        }
        ctrl.current = null;
        setBusy(false);
    };

    const onDrop = (e: DragEvent<HTMLElement>) => {
        e.preventDefault();
        setDragOver(false);
        if (!busy && e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
    };

    if (status === "loading") {
        return (
            <Page>
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Opening file request…</p>
            </Page>
        );
    }
    if (status !== "ok" || !request) {
        const copy =
            status === "closed"
                ? { title: "This request is closed", body: "It's no longer accepting files. Ask the person who sent you the link for a new one." }
                : status === "offline"
                  ? { title: "Can't reach Drive", body: "Check your connection and reload the page." }
                  : { title: "Link unavailable", body: "This file request link is invalid or has been removed." };
        return (
            <Page>
                <ShieldAlert className="h-10 w-10 text-amber-500" aria-hidden />
                <h1 className="text-lg font-semibold">{copy.title}</h1>
                <p className="max-w-sm text-center text-sm text-muted-foreground">{copy.body}</p>
            </Page>
        );
    }

    const ready = entries.filter((e) => e.status === "ready").length;
    const sent = entries.filter((e) => e.status === "done").length;
    const allSent = sent > 0 && entries.every((e) => e.status === "done" || e.fatal);

    return (
        <Page>
            <div className="w-full max-w-lg rounded-xl border bg-card p-5 text-card-foreground shadow-sm sm:p-8">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10">
                        <Inbox className="h-5 w-5 text-primary" aria-hidden />
                    </div>
                    <div className="min-w-0">
                        <h1 className="break-words text-lg font-semibold leading-snug">{request.title}</h1>
                        <p className="mt-0.5 break-words text-sm text-muted-foreground">{request.ownerEmail} is asking you for files</p>
                    </div>
                </div>
                {request.instructions ? (
                    <p className="mt-4 whitespace-pre-wrap break-words rounded-lg bg-muted/60 p-3 text-sm">{request.instructions}</p>
                ) : null}

                <form
                    className="mt-5 space-y-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void send();
                    }}
                >
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <label htmlFor="fr-name" className="mb-1 block text-xs font-medium text-muted-foreground">Your name</label>
                            <Input id="fr-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoComplete="name" disabled={busy} required />
                        </div>
                        <div>
                            <label htmlFor="fr-email" className="mb-1 block text-xs font-medium text-muted-foreground">Your email</label>
                            <Input id="fr-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={256} autoComplete="email" disabled={busy} required />
                        </div>
                    </div>

                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => fileInput.current?.click()}
                        onDragOver={(e) => {
                            e.preventDefault();
                            if (!busy) setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={onDrop}
                        className={cn(
                            "flex w-full flex-col items-center gap-1.5 rounded-lg border-2 border-dashed px-4 py-6 text-center text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60",
                            dragOver ? "border-primary bg-primary/5" : "border-input hover:bg-accent"
                        )}
                    >
                        <Upload className="h-6 w-6 text-primary" aria-hidden />
                        <span className="font-medium">Drop files here or choose files</span>
                        <span className="text-xs text-muted-foreground">Up to {formatBytes(maxBytes)} each</span>
                    </button>
                    <input
                        ref={fileInput}
                        type="file"
                        multiple
                        hidden
                        onChange={(e) => {
                            if (e.target.files?.length) addFiles(e.target.files);
                            e.target.value = "";
                        }}
                    />

                    {entries.length > 0 ? (
                        <ul className="divide-y rounded-lg border">
                            {entries.map((e) => (
                                <li key={e.id} className="flex items-center gap-3 px-3 py-2">
                                    <ItemIcon item={{ kind: "File", name: e.file.name, contentType: e.file.type || null }} />
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-sm" title={e.file.name}>{e.file.name}</div>
                                        {e.status === "uploading" ? (
                                            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={Math.round(e.progress * 100)} aria-valuemin={0} aria-valuemax={100} aria-label={`Uploading ${e.file.name}`}>
                                                <div className="h-full rounded-full bg-primary transition-[width] duration-200" style={{ width: `${Math.round(e.progress * 100)}%` }} />
                                            </div>
                                        ) : (
                                            <div className={cn("truncate text-xs", e.status === "error" ? "text-destructive" : "text-muted-foreground")}>
                                                {e.status === "error" ? e.error ?? "Upload failed" : e.status === "done" ? "Sent" : formatBytes(e.file.size)}
                                            </div>
                                        )}
                                    </div>
                                    {e.status === "done" ? (
                                        <CircleCheck className="h-5 w-5 shrink-0 text-emerald-500" aria-label="Sent" />
                                    ) : e.status === "error" && !e.fatal && !busy ? (
                                        <>
                                            <CircleAlert className="h-4 w-4 shrink-0 text-destructive" aria-hidden />
                                            <IconButton label={`Try ${e.file.name} again`} onClick={() => update(e.id, { status: "ready", error: undefined })}>
                                                <RotateCcw className="h-4 w-4" />
                                            </IconButton>
                                        </>
                                    ) : null}
                                    {e.status === "uploading" ? (
                                        <IconButton label={`Stop uploading ${e.file.name}`} onClick={() => ctrl.current?.abort()}>
                                            <X className="h-4 w-4" />
                                        </IconButton>
                                    ) : e.status !== "done" && !busy ? (
                                        <IconButton label={`Remove ${e.file.name}`} onClick={() => setEntries((es) => es.filter((x) => x.id !== e.id))}>
                                            <X className="h-4 w-4" />
                                        </IconButton>
                                    ) : null}
                                </li>
                            ))}
                        </ul>
                    ) : null}

                    {formError ? <p className="text-sm text-destructive" role="alert">{formError}</p> : null}
                    {allSent && !busy ? (
                        <p className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300" role="status">
                            <CircleCheck className="h-4 w-4 shrink-0" aria-hidden />
                            {sent === 1 ? "Your file was sent." : `Your ${sent} files were sent.`} You can add more or close this page.
                        </p>
                    ) : null}

                    <div className="flex justify-end">
                        <Button type="submit" disabled={busy || ready === 0} className="gap-2">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                            {busy ? "Uploading…" : ready > 1 ? `Upload ${ready} files` : "Upload"}
                        </Button>
                    </div>
                </form>
            </div>
            <p className="max-w-lg text-center text-[11px] text-muted-foreground">
                Files go to {request.ownerEmail}'s Drive, named with your name. You won't see anything else in the folder.
                {request.expiresAt ? ` This request closes ${formatDateTime(request.expiresAt)}.` : ""}
            </p>
        </Page>
    );
}

function IconButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
            {children}
        </button>
    );
}

function Page({ children }: { children: ReactNode }) {
    return <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-muted/20 p-4 sm:p-6">{children}</div>;
}
